import React, { useState } from "react";
import Typography from "@mui/material/Typography";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import { styled } from "@mui/material/styles";
import {
  Box,
  Tooltip,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Popover,
  Button,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import CustomTypography from "../../globalStyles/fonts";
import DeleteConfirmationModal from "../deleteConfirmationModal";
import { isMobile } from "../../globalStyles/mobile";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ViewIcon from "../../globalStyles/icons/vieewIcon";
import EditIcon from "../../globalStyles/icons/editIcon";
import DeleteIcon from "../../globalStyles/icons/deleteIcon";
import TableHeader from "../table-header";
import { useDarkMode } from "../context/darkModeContext";
import CustomerDetailsModal from "../customer-details-modal";
import { customerService } from "../../utils/services/customerService";
import { toast } from "react-toastify";

const StyledTableCell = styled(TableCell)({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "var(--table-header-bg)",
    border: "none",
    padding: 8,
    position: "sticky",
    top: 0,
    zIndex: 10,
  },
  [`&.${tableCellClasses.body}`]: { fontSize: 12, padding: 6 },
});

const StyledTableRow = styled(TableRow)(() => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "var(--bg-paper)",
    border: "1px solid var(--border-color)",
  },
  "&:nth-of-type(even)": {
    backgroundColor: "var(--row-bg-even)",
    border: "1px solid var(--border-color)",
  },
  "&:last-child td, &:last-child th": { border: 0 },
}));

const CustomerTableCell = ({
  customer,
  handleViewClick,
  handleEditClick,
  clientId,
  colors,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const primaryAddress = customer?.address?.[0];

  const handlePopoverOpen = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = (e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const handleActionClick = (action) => {
    handlePopoverClose();
    switch (action) {
      case "view":
        handleViewClick(customer);
        break;
      case "edit":
        handleEditClick(customer);
        break;
      case "delete":
        setOpenDeleteModal(true);
        break;
      default:
        break;
    }
  };

  return (
    <>
      <StyledTableRow>
        <StyledTableCell>
          <CustomTypography fontSize={13}>
            {customer?.name || "-"}
          </CustomTypography>
        </StyledTableCell>
        <StyledTableCell>
          <CustomTypography fontSize={13}>
            {customer?.email || "-"}
          </CustomTypography>
        </StyledTableCell>
        <StyledTableCell>
          <CustomTypography fontSize={13}>
            {customer?.phone || "-"}
          </CustomTypography>
        </StyledTableCell>
        <StyledTableCell>
          <CustomTypography fontSize={13}>
            {primaryAddress?.city || "-"}
          </CustomTypography>
        </StyledTableCell>
        <StyledTableCell>
          <CustomTypography fontSize={13}>
            {primaryAddress?.province || "-"}
          </CustomTypography>
        </StyledTableCell>
        <StyledTableCell>
          <CustomTypography fontSize={13}>
            {primaryAddress?.country || "-"}
          </CustomTypography>
        </StyledTableCell>
        <StyledTableCell>
          <Box>
            <IconButton
              aria-label="more actions"
              onClick={handlePopoverOpen}
              sx={{
                color: open ? "primary.main" : "inherit",
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
            <Popover
              open={open}
              anchorEl={anchorEl}
              onClose={handlePopoverClose}
              anchorOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  padding: "10px",
                }}
              >
                <Button
                  style={{
                    color: "var(--text-tertiary)",
                    justifyContent: "flex-start",
                    textTransform: "none",
                  }}
                  aria-label="view"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleActionClick("view");
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-start"
                    width="100%"
                    gap={1}
                  >
                    <ViewIcon />
                    <Typography
                      fontFamily={"poppins"}
                      fontSize={"16px"}
                      color={"var(--text-tertiary)"}
                      fontWeight={500}
                    >
                      View
                    </Typography>
                  </Box>
                </Button>
                {!clientId && (
                  <>
                    <Button
                      style={{
                        color: "var(--text-tertiary)",
                        justifyContent: "flex-start",
                        textTransform: "none",
                      }}
                      aria-label="edit"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleActionClick("edit");
                      }}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="flex-start"
                        width="100%"
                        gap={1}
                      >
                        <EditIcon />
                        <Typography
                          fontFamily={"poppins"}
                          fontSize={"16px"}
                          color={"var(--text-tertiary)"}
                          fontWeight={500}
                        >
                          Edit
                        </Typography>
                      </Box>
                    </Button>
                    <Button
                      style={{
                        justifyContent: "flex-start",
                        textTransform: "none",
                      }}
                      aria-label="delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleActionClick("delete");
                      }}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="flex-start"
                        width="100%"
                        gap={1}
                      >
                        <DeleteIcon />
                        <Typography
                          fontFamily={"poppins"}
                          fontSize={"16px"}
                          fontWeight={500}
                          color={"var(--text-tertiary)"}
                        >
                          Delete
                        </Typography>
                      </Box>
                    </Button>
                  </>
                )}
              </Box>
            </Popover>
          </Box>
          <DeleteConfirmationModal
            title={`Delete customer "${customer?.name}"?`}
            open={openDeleteModal}
            handleClose={() => setOpenDeleteModal(false)}
            handleDelete={async () => {
              try {
                await customerService.delete(customer._id);
                toast.success("Customer deleted successfully");
                setOpenDeleteModal(false);
                handleEditClick(null); // trigger refresh
              } catch (e) {
                toast.error("Failed to delete customer");
              }
            }}
          />
        </StyledTableCell>
      </StyledTableRow>
    </>
  );
};

const CustomerList = ({
  data = [],
  onEditData,
  onDeleteData,
  filterData,
  onChangeFilter,
  currentPage,
  itemsPerPage,
  clientId,
}) => {
  const { colors } = useDarkMode();
  const [viewCustomer, setViewCustomer] = useState(null);

  const columns = [
    { key: "name", title: "Name", sortKey: "name" },
    { key: "email", title: "Email", sortKey: "email" },
    { key: "phone", title: "Phone", sortKey: "phone" },
    { key: "address.city", title: "City" },
    { key: "address.province", title: "Province" },
    { key: "address.country", title: "Country" },
    { key: "actions", title: "" },
  ];

  return (
    <>
      <TableContainer
        sx={{
          maxHeight: "calc(100vh - 210px)",
          height: "100%",
          overflow: "auto",
        }}
      >
        <Table stickyHeader sx={{ minWidth: 650 }}>
          <TableHead>
            <StyledTableRow>
              {columns.map((col) => (
                <StyledTableCell key={col.key}>
                  <TableHeader
                    filterData={filterData}
                    onChangeFilter={onChangeFilter}
                    key_={col.key}
                    title={col.title}
                  />
                </StyledTableCell>
              ))}
            </StyledTableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 ? (
              <StyledTableRow>
                <StyledTableCell colSpan={columns.length} align="center">
                  <Typography>No customers found</Typography>
                </StyledTableCell>
              </StyledTableRow>
            ) : (
              data.map((customer) => (
                <CustomerTableCell
                  key={customer._id}
                  customer={customer}
                  colors={colors}
                  clientId={clientId}
                  handleViewClick={(c) => setViewCustomer(c)}
                  handleEditClick={(c) => {
                    if (c) onEditData(c);
                    else onDeleteData();
                  }}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <CustomerDetailsModal
        open={!!viewCustomer}
        onClose={() => setViewCustomer(null)}
        customer={viewCustomer}
      />
    </>
  );
};

export default CustomerList;
